import type { ProviderInfo } from "@/src/lib/types/provider.types";
import { diagnosisCodeMatches } from "@/src/lib/utils/diagnosis-code-matcher";
import type { Diagnosis } from "@/src/models/documents/referral";

/**
 * Configuration for a single provider, including their diagnosis rules
 */
export interface ProviderConfig {
  name: string;
  ehrId: string;
  acceptedDiagnoses: string[];
  excludedDiagnoses: string[];
  acceptedServiceLines: string[];
  excludedServiceLines: string[];
  acceptedInsurance: string[];
  excludedInsurance: string[];
  acceptedPriority: string[];
  excludedPriority: string[];
}

/**
 * Configuration structure for a group of providers
 */
export interface ProvidersConfig {
  id: string;
  name: string;
  providers: Record<string, ProviderConfig>;
}

/**
 * Rules for matching providers to ICD-10 diagnosis codes
 */
export type ProviderICD10Rules = {
  include: readonly string[];
  exclude: readonly string[];
};

interface ProviderLookupInfo {
  name: string;
  ehrId: string;
}

/**
 * Generic function to find providers that match a given list of diagnoses.
 *
 * @param diagnosisList - The list of diagnoses to match against
 * @param providerRules - A map of provider identifiers (e.g., NPI) to their inclusion rules
 * @param providerInfo - A map of provider identifiers to their display information (name, ehrId)
 * @param defaultProvider - Optional default provider to return if no matches are found
 * @returns An array of ProviderInfo for all matching providers, or the default provider if none match
 */
/**
 * Extracts practitioner NPI information from a ProvidersConfig.
 */
export function extractPractitionerNPIs(
  config: ProvidersConfig
): Record<string, { npi: string; name: string; ehrId: string }> {
  return Object.fromEntries(
    Object.entries(config.providers).map(([npi, provider]) => [
      npi,
      {
        npi,
        name: provider.name,
        ehrId: provider.ehrId,
      },
    ])
  );
}

/**
 * Extracts provider ICD-10 rules from a ProvidersConfig.
 */
export function extractProviderICD10Map(config: ProvidersConfig): Record<string, ProviderICD10Rules> {
  return Object.fromEntries(
    Object.entries(config.providers).map(([npi, provider]) => [
      npi,
      {
        include: provider.acceptedDiagnoses as readonly string[],
        exclude: provider.excludedDiagnoses as readonly string[],
      },
    ])
  );
}

export function getProvidersForDiagnosis(
  diagnosisList: Diagnosis[],
  providerRules: Record<string, ProviderICD10Rules>,
  providerInfo: Record<string, ProviderLookupInfo>,
  defaultProvider?: ProviderInfo
): ProviderInfo[] {
  const uniqueCodes = Array.from(new Set(diagnosisList.map((d) => d.diagnosisCode?.trim() || "").filter(Boolean)));
  const matched = new Set<ProviderInfo>();

  for (const providerId of Object.keys(providerRules)) {
    const rules = providerRules[providerId];
    const isIncluded = uniqueCodes.some((code) => rules.include.some((pattern) => diagnosisCodeMatches(pattern, code)));

    if (isIncluded) {
      const info = providerInfo[providerId];

      if (info) {
        matched.add({ name: info.name, ehrId: info.ehrId });
      }
    }
  }

  if (matched.size === 0 && defaultProvider) {
    matched.add(defaultProvider);
  }

  return Array.from(matched);
}
